import { Button, Empty, Flex, Input, Typography } from 'antd'
import { Plus, Search } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  useDebouncedValue,
  useDocumentTitle,
  useQueryParams,
  useQueryUpdate,
  useSessionContext
} from 'src/hooks'
import { paths } from 'src/router'
import { defaultFilters } from 'src/utils'

import { OrganizationList } from '../components'

import { useStyles } from './styles'

const { Paragraph, Title } = Typography

export const OrganizationsPage = () => {
  const { t } = useTranslation()
  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()
  const { user } = useSessionContext()
  const navigate = useNavigate()
  const { styles } = useStyles()

  useDocumentTitle(t('workspaces.pageTitle'))

  const urlSearch = params?.search ?? ''
  const [searchDraft, setSearchDraft] = useState(urlSearch)
  const [searchSeen, setSearchSeen] = useState(urlSearch)
  if (urlSearch !== searchSeen) {
    setSearchSeen(urlSearch)
    setSearchDraft(urlSearch)
  }
  const debouncedSearch = useDebouncedValue(searchDraft, 300)
  useEffect(() => {
    if (debouncedSearch === searchDraft && debouncedSearch !== urlSearch) {
      updateQuery({
        ...params,
        page: defaultFilters.page,
        search: debouncedSearch
      })
    }
  }, [debouncedSearch, searchDraft, urlSearch, params, updateQuery])

  const q = urlSearch.toLowerCase().trim()
  const total = user?.organizations?.length ?? 0
  const filteredOrganizations = user?.organizations.filter((organization) => {
    if (!q) return true
    const name = organization?.name?.toLowerCase() ?? ''
    const domain = organization?.domain_name?.toLowerCase() ?? ''

    return name.includes(q) || domain.includes(q)
  })
  const hasResults = (filteredOrganizations?.length ?? 0) > 0
  const isFiltering = q.length > 0

  return (
    <Flex vertical gap={20} className={styles.page}>
      <Flex justify="space-between" align="flex-end" wrap="wrap" gap={16}>
        <Flex vertical gap={6}>
          <Flex align="center" gap={10}>
            <Title level={3} style={{ margin: 0 }}>
              {t('workspaces.pageTitle')}
            </Title>
            <span className={styles.countChip}>{total}</span>
          </Flex>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('workspaces.pickerDescription')}
          </Paragraph>
        </Flex>
        <Button
          onClick={() => navigate(paths.workspaces.new)}
          type="primary"
          size="large"
          icon={<Plus size={16} />}
        >
          {t('add_organization')}
        </Button>
      </Flex>

      <Input
        allowClear
        size="large"
        prefix={
          <Search
            size={16}
            color="var(--ant-color-text-tertiary)"
            style={{ marginInlineEnd: 6 }}
          />
        }
        placeholder={t('workspaces.searchPlaceholder')}
        value={searchDraft}
        onChange={(e) => setSearchDraft(e.target.value)}
        className={styles.searchInput}
      />

      {hasResults ? (
        <OrganizationList filteredOrganizations={filteredOrganizations} />
      ) : (
        <div className={styles.empty}>
          <Empty
            description={
              isFiltering
                ? t('workspaces.emptyFiltered')
                : t('workspaces.empty')
            }
          >
            {isFiltering ? (
              <Button onClick={() => setSearchDraft('')}>
                {t('workspaces.clearSearch')}
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<Plus size={16} />}
                onClick={() => navigate(paths.workspaces.new)}
              >
                {t('add_organization')}
              </Button>
            )}
          </Empty>
        </div>
      )}
    </Flex>
  )
}
